/**
 * Contest surveys — sport-specific. Questions are draft placeholders until product finalizes.
 * @see docs/BRAND_GUIDELINES_ENYCGAMES.md (learning & assessment identity)
 */

export interface ContestSurveyQuestion {
  id: string;
  prompt: string;
  type: 'single' | 'multi' | 'text' | 'scale';
  options?: string[];
  /** When true, question is shown but not yet collected for scoring */
  draft?: boolean;
}

export interface ContestSurveyDefinition {
  sport: string;
  title: string;
  status: 'draft' | 'published';
  intro: string;
  questions: ContestSurveyQuestion[];
}

const DEFAULT_DRAFT_QUESTIONS: ContestSurveyQuestion[] = [
  {
    id: 'experience',
    prompt: 'How many years have you been playing this sport?',
    type: 'single',
    options: ['Less than 1', '1–3', '3–5', '5+'],
    draft: true,
  },
  {
    id: 'practice_frequency',
    prompt: 'How often do you practice or train?',
    type: 'single',
    options: ['Rarely', '1–2 times / week', '3–4 times / week', 'Almost daily'],
    draft: true,
  },
  {
    id: 'confidence_goal',
    prompt: 'What is your main goal on EncycGames Contests?',
    type: 'single',
    options: ['Learn fundamentals', 'Track improvement', 'Compete / rank', 'Coach feedback'],
    draft: true,
  },
  {
    id: 'feedback',
    prompt: 'Anything else we should know about your training setup? (optional)',
    type: 'text',
    draft: true,
  },
];

const SURVEYS: Record<string, ContestSurveyDefinition> = {
  Badminton: {
    sport: 'Badminton',
    title: 'Badminton contest survey',
    status: 'draft',
    intro:
      'Short questions about your badminton background. Final questions will be published before contest scoring uses survey data.',
    questions: [
      ...DEFAULT_DRAFT_QUESTIONS,
      {
        id: 'badminton_grip',
        prompt: 'Which grip do you use most often?',
        type: 'single',
        options: ['Forehand', 'Backhand', 'Both equally', 'Not sure'],
        draft: true,
      },
    ],
  },
  Football: {
    sport: 'Football',
    title: 'Football contest survey',
    status: 'draft',
    intro:
      'Short questions about your football background. Final questions will be published before contest scoring uses survey data.',
    questions: [
      ...DEFAULT_DRAFT_QUESTIONS,
      {
        id: 'football_position',
        prompt: 'Preferred position (if any)?',
        type: 'single',
        options: ['Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'No preference'],
        draft: true,
      },
    ],
  },
  Cricket: {
    sport: 'Cricket',
    title: 'Cricket contest survey',
    status: 'draft',
    intro:
      'Short questions about your cricket background. Final questions will be published before contest scoring uses survey data.',
    questions: [
      ...DEFAULT_DRAFT_QUESTIONS,
      {
        id: 'cricket_role',
        prompt: 'Which role do you identify with most?',
        type: 'single',
        options: ['Batter', 'Bowler', 'All-rounder', 'Wicket-keeper', 'Not sure'],
        draft: true,
      },
    ],
  },
};

export function getContestSurvey(sportName: string): ContestSurveyDefinition {
  const key = String(sportName || '').trim();
  if (SURVEYS[key]) return SURVEYS[key];
  return {
    sport: key || 'Sport',
    title: `${key || 'Sport'} contest survey`,
    status: 'draft',
    intro:
      'Survey questions for this sport are not finalized yet. A draft template is shown for layout only.',
    questions: DEFAULT_DRAFT_QUESTIONS,
  };
}
